import * as Accordion from "@radix-ui/react-accordion";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Callout, Card, Flex, Spinner, Text } from '@radix-ui/themes';
import { EmptyState, ErrorState } from "../../components/common/EmptyState";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useGetStudentGradeSubjectsQuery } from "../../hooks/useGetStudentGradeSubjectsQuery";
import { useGetTeacheIdByUserId } from "../../hooks/useGetTeacheIdByUserId";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { getMyStudentAssessments } from "../../services/school/student/getStudentsAssessments";
import { StudentAssessmentType } from "../../types/Assessment";

export const StudentGrades = () => {
  const { UserId } = useSelector((state: RootState) => state.login.userData);
  const { data: studentData, isLoading: isLoadingStudentData } = useGetTeacheIdByUserId(UserId);

  const { isLoading: isLoadingStudentAssessments, data: studentAssessments, isError: isErrorStudentAssessments, isSuccess } = useQuery(
    ['fetchStudentAssessments'],
    async () => {
      try {
        const studentsAssessments = await getMyStudentAssessments({ PageNumber: 1, PageSize: 400, IdStudent: studentData?.idStudent });
        return studentsAssessments;
      } catch (error) {
        console.error(error);
      }
    },
    {
      enabled: !!studentData?.idStudent,
      refetchOnWindowFocus: true,
      retry: false
    }
  );

  const { data: StudentGradeSubjects } = useGetStudentGradeSubjectsQuery(
    { PageNumber: 1, PageSize: 400, IdStudent: studentData?.idStudent },
    {
      enabled: !!studentData?.idStudent && isSuccess,
      refetchOnWindowFocus: true,
      retry: false
    }
  );

  if (isLoadingStudentData || isLoadingStudentAssessments) {
    return <Spinner size="3" />;
  }

  if (isErrorStudentAssessments) {
    return (
      <ErrorState
        title="Erro ao carregar avaliações"
        description="Não foi possível obter as suas avaliações. Por favor, tente novamente."
      />
    );
  }

  // Novo agrupamento: academicYear -> level -> semester -> subject -> assessment type
  const groupedByAcademicYear = studentAssessments?.items.reduce((acc: Record<string, any>, assessment: StudentAssessmentType) => {
    const academicYear = assessment.academicYear || "Ano letivo desconhecido";
    const level = `${assessment.level}º Ano`;
    const semester = `${assessment.semester}º Semestre`;
    const subject = assessment.subject || "Disciplina desconhecida";
    const type = assessment.assessment || "Avaliação sem descrição";

    if (!acc[academicYear]) acc[academicYear] = {};
    if (!acc[academicYear][level]) acc[academicYear][level] = {};
    if (!acc[academicYear][level][semester]) acc[academicYear][level][semester] = {};
    if (!acc[academicYear][level][semester][subject]) acc[academicYear][level][semester][subject] = {};
    if (!acc[academicYear][level][semester][subject][type]) acc[academicYear][level][semester][subject][type] = [];

    acc[academicYear][level][semester][subject][type].push(assessment);
    return acc;
  }, {});

  if (!studentData || !studentData.idStudent) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            Entre como estudante &nbsp;
            <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Callout.Text>
        </Callout.Root>
      </Flex>
    );
  }

  return (
    <Card>
      <Text as="p" size="5" weight="bold" mb="2">Notas</Text>
      {(!groupedByAcademicYear || Object.keys(groupedByAcademicYear).length === 0) && (
        <EmptyState
          title="Sem notas publicadas"
          description="Ainda não existem avaliações publicadas para apresentar."
        />
      )}
      <Accordion.Root type="multiple">
        {groupedByAcademicYear &&
          // Ordenar anos letivos em ordem decrescente (mais recente primeiro)
          Object.keys(groupedByAcademicYear)
            .sort((a, b) => b.localeCompare(a))
            .map((academicYear) => (
              <Accordion.Item key={academicYear} value={academicYear}>
                <Accordion.Header>
                  <Accordion.Trigger>Ano Letivo {academicYear}</Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content>
                  <Accordion.Root type="multiple">
                    {Object.keys(groupedByAcademicYear[academicYear])
                      .sort((a, b) => {
                        // Ordenar níveis (1º Ano, 2º Ano, etc.)
                        const numA = parseInt(a);
                        const numB = parseInt(b);
                        return numA - numB;
                      })
                      .map((level) => (
                        <Accordion.Item key={level} value={level}>
                          <Accordion.Header>
                            <Accordion.Trigger>{level}</Accordion.Trigger>
                          </Accordion.Header>
                          <Accordion.Content>
                            <Accordion.Root type="multiple">
                              {Object.keys(groupedByAcademicYear[academicYear][level])
                                .sort((a, b) => {
                                  // Ordenar semestres
                                  const numA = parseInt(a);
                                  const numB = parseInt(b);
                                  return numA - numB;
                                })
                                .map((semester) => (
                                  <Accordion.Item key={semester} value={semester}>
                                    <Accordion.Header>
                                      <Accordion.Trigger>{semester}</Accordion.Trigger>
                                    </Accordion.Header>
                                    <Accordion.Content>
                                      {Object.keys(groupedByAcademicYear[academicYear][level][semester]).map((subject, index) => {
                                        const assessmentsByType = groupedByAcademicYear[academicYear][level][semester][subject];

                                        const publishedAssessmentsByType = Object.keys(assessmentsByType).reduce((acc, type) => {
                                          const published = assessmentsByType[type].filter((a: { isPublished: any; }) => a.isPublished);
                                          if (published.length > 0) {
                                            acc[type] = published;
                                          }
                                          return acc;
                                        }, {} as Record<string, StudentAssessmentType[]>);

                                        const firstAssessment = assessmentsByType[Object.keys(assessmentsByType)[0]][0];
                                        const subjectGradeInfo = StudentGradeSubjects?.items.find(
                                          i => (i.idSubject === firstAssessment.idSubject &&
                                            i.level === firstAssessment.level &&
                                            i.semester === firstAssessment.semester &&
                                            i.academicYear === firstAssessment.academicYear)
                                        );

                                        return (
                                          <Card key={subject} mb="3" style={{ overflow: 'auto' }}>
                                            <Flex key={`${subject}_${index}`} gap='5' wrap="wrap">
                                              <Text size="4" weight="bold">{subject}</Text>

                                              {Object.entries(publishedAssessmentsByType).map(([type, assessments]) => (
                                                <Flex key={type} direction="column" gap="2">
                                                  <Text size="3">{type}</Text>
                                                  {assessments.map(assessment => (
                                                    <Text key={assessment.id}>{assessment.grade}</Text>
                                                  ))}
                                                </Flex>
                                              ))}

                                              {/* Nota Final */}
                                              <Flex direction="column" gap="2">
                                                <Text size="3" weight="medium">Nota Final</Text>
                                                <Text weight="medium">
                                                  {subjectGradeInfo?.grade != null && !isNaN(Number(subjectGradeInfo.grade))
                                                    ? Number(subjectGradeInfo.grade).toFixed(1)
                                                    : '--'}
                                                </Text>
                                              </Flex>

                                              {/* Resultado */}
                                              <Flex direction="column" gap="2">
                                                <Text size="3" weight="bold">Resultado</Text>
                                                <Text weight="bold"
                                                  color={
                                                    subjectGradeInfo?.result &&
                                                      ["Aprovado", "Dispensado", "Admitido"].includes(subjectGradeInfo.result)
                                                      ? "green"
                                                      : "red"
                                                  }
                                                >
                                                  {subjectGradeInfo?.result ?? '--'}
                                                </Text>
                                              </Flex>
                                            </Flex>
                                          </Card>
                                        );
                                      })}
                                    </Accordion.Content>
                                  </Accordion.Item>
                                ))}
                            </Accordion.Root>
                          </Accordion.Content>
                        </Accordion.Item>
                      ))}
                  </Accordion.Root>
                </Accordion.Content>
              </Accordion.Item>
            ))}
      </Accordion.Root>
    </Card>
  );
};